import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import AsyncSelect from "react-select/async";
import AsyncCreatable from "react-select/async-creatable";
import { TreeSelect } from "antd";
import { InputTextarea } from "primereact/inputtextarea";
import { SelectButton } from "primereact/selectbutton";
import { QueryBuilder, prepareRuleGroup } from "react-querybuilder";
import Select from "../../inputs/select";
import Radio from "../../inputs/radio";
import {
	QueryBuilderValueEditor,
	QueryBuilderSelectField,
	QueryBuilderCombinatorSelector,
	QueryBuilderButton,
	QueryBuilderCloseButton,
} from "../../inputs/querybuilder";
import { getCategories, getGARMTaxonomy, getSentimentTaxonomy } from "../../../services/cohort";
import { getKeywordsByTag, getTaxonomyByTag } from "../../../services/tag";
import CriterionSummary from "./CriterionSummary";
import * as CONST from "../../../utilities/constants";
import eyeIcon from "../../../assets/svgs/eyeopen.svg";
import "./RealtimeSiteContentCard.scss";

/**
 * RealtimeSiteContentCard - Simplified criteria card for contextual site content targeting
 *
 * Data format: {
 *   action: "include" | "exclude",
 *   criteria: {
 *     type: "categories" | "keywords" | "urls" | "garm" | "sentiments" | "default" | "all",
 *     categories: [...],  // for type="categories"
 *     keywords: [...],    // for type="keywords"
 *     urls: [...],        // for type="urls"
 *     garm: [...],        // for type="garm"
 *     sentiments: [...],  // for type="sentiments"
 *     taxonomy: {...},    // for type="default"
 *   }
 * }
 */

// Criteria type options for contextual targeting
const CRITERIA_OPTIONS = [
	{ value: "categories", label: "IAB Categories", info: "Target sites falling under specific content categories" },
	{ value: "keywords", label: "Keywords", info: "Target keywords or topics that match the content on your site" },
	{ value: "urls", label: "URLs", info: "Target specific sites" },
	{ value: "default", label: "Custom Content Taxonomy", info: "Target sites falling under specific criteria", isTaxonomy: true },
	{ value: "garm", label: "Brand Safety + Suitability", info: "Target sites by brand safety risk level" },
	{ value: "sentiments", label: "Sentiment Targeting", info: "Target sites with specific sentiments" },
];

// Generate criteria options including custom criteria from configuration
// Filters based on customTaxonomies settings
const getCriteriaOptions = (criteriaConfiguration = [], customTaxonomies = []) => {
	// Filter base options based on customTaxonomies settings
	const filteredOptions = CRITERIA_OPTIONS
		.filter((opt) => !opt.isTaxonomy || customTaxonomies.includes("default"))
		.filter((opt) => opt.value !== "garm" || customTaxonomies.includes("garm"))
		.filter((opt) => opt.value !== "sentiments" || customTaxonomies.includes("sentiments"));
	const customOptions = criteriaConfiguration
		.filter((el) => (el.targetTypes ?? []).includes("contextual"))
		.map((el) => ({
			value: `custom|${el.event}`,
			label: `${el.publisherName ?? "Custom"}: ${el.label}`,
			info: el.info ?? `Target sites falling under specific ${el.publisherName ?? "custom"} criteria`,
			isCustom: true,
			filters: (el.filters || []).map((f) => {
				const filter = {
					name: f.field,
					label: f.label,
					operators: CONST.queryBuilderOperators.filter((op) => f.operators.includes(op.name)),
					customDataType: "custom_criteria",
					autocomplete: false,
					eventName: "default",
					valueMap: f.valueMap ?? [],
					tagId: el.tagId,
				};
				switch (f.type) {
					case "integer":
						filter.inputType = "number";
						break;
					case "boolean":
						filter.inputType = "boolean";
						filter.defaultValue = true;
						filter.valueEditorType = "checkbox";
						break;
					case "date":
						filter.inputType = "date";
						filter.operators = CONST.queryBuilderDateOperators;
						break;
					case "string":
						filter.inputType = "string";
						filter.autocomplete = true;
						filter.multiValue = true;
						break;
				}
				return filter;
			}),
		}));
	return [...filteredOptions, ...customOptions];
};

function RealtimeSiteContentCard({ data, onChange, isReadOnly, isCollapsed, tagId, criteriaConfiguration = [], customTaxonomies = [], cardId }) {

	// Generate criteria options including custom criteria (filtered by customTaxonomies)
	const criteriaOptions = getCriteriaOptions(criteriaConfiguration, customTaxonomies);

	// Get current action
	const getCurrentAction = () => {
		return CONST.ruleActions.find((a) => a.value === data?.action) || CONST.ruleActions[0];
	};

	// Get current criteria type
	const getCurrentCriteriaType = () => {
		const criteriaType = data?.criteria?.type;
		if (!criteriaType) return null;

		// Handle custom criteria type
		if (criteriaType === "custom") {
			const customEventType = data?.criteria?.custom?.type;
			return criteriaOptions.find((opt) => opt.value === `custom|${customEventType}`) || null;
		}

		return criteriaOptions.find((opt) => opt.value === criteriaType) || null;
	};

	// Handle action change
	const handleActionChange = (action) => {
		onChange({
			...data,
			action: action.value,
		});
	};

	// Handle criteria type change
	const handleCriteriaTypeChange = (option) => {
		// Handle custom criteria type
		if (option.isCustom) {
			const eventName = option.value.split("|")[1];
			const filters = option.filters || [];
			const firstFilter = filters[0];
			// Initialize with one empty rule (matching original builder behavior)
			const initialRule = firstFilter
				? {
						field: firstFilter.name,
						operator: firstFilter.operators?.[0]?.name || "=",
						value: firstFilter.inputType === "boolean" ? true : "",
					}
				: { field: "", operator: "=", value: "" };
			onChange({
				...data,
				criteria: {
					type: "custom",
					custom: {
						type: eventName,
						filterBy: { combinator: "or", rules: [initialRule] },
					},
				},
			});
		} else {
			onChange({
				...data,
				criteria: {
					type: option.value,
				},
			});
		}
	};

	// Handle criteria data change (from sub-components)
	const handleCriteriaDataChange = (criteriaData) => {
		onChange({
			...data,
			criteria: {
				...data?.criteria,
				...criteriaData,
			},
		});
	};

	// Collapsed view
	if (isCollapsed) {
		return (
			<div className="realtime-site-content-card collapsed">
				<CriterionSummary
					type="realtimeSiteContent"
					data={data}
					criteriaConfiguration={criteriaConfiguration}
				/>
			</div>
		);
	}

	// Expanded view
	return (
		<div className="realtime-site-content-card expanded">
			{/* Include/Exclude action */}
			<div className="rule-action">
				<Radio
					options={CONST.ruleActions}
					name={`realtime-content-action-${cardId}`}
					style="list"
					disabled={isReadOnly}
					required={false}
					selected={getCurrentAction()}
					onInput={handleActionChange}
				/>
			</div>

			{/* Criteria type selector */}
			<Select
				label="Select Targeting Criteria"
				name="criteria-type"
				options={criteriaOptions}
				selected={getCurrentCriteriaType()}
				onSubmit={handleCriteriaTypeChange}
				disabled={isReadOnly}
				required={true}
				search={false}
				info={getCurrentCriteriaType()?.info}
			/>

			{/* Criteria-specific content */}
			{renderCriteriaContent(data?.criteria?.type || "all", getCurrentCriteriaType(), data?.criteria, handleCriteriaDataChange, isReadOnly, tagId)}
		</div>
	);
}

// Render function for criteria-specific content (plain function, not a component,
// to keep CustomCriteriaSelector in the same React tree position as the parent)
function renderCriteriaContent(criteriaType, criteriaTypeOption, data, onChange, isReadOnly, tagId) {
	// Handle custom criteria (QueryBuilder) — always render when type is "custom"
	// to prevent unmount/remount which causes react-querybuilder to regenerate IDs
	if (criteriaType === "custom") {
		return (
			<CustomCriteriaSelector
				data={data}
				onChange={onChange}
				isReadOnly={isReadOnly}
				filters={criteriaTypeOption?.filters || []}
			/>
		);
	}

	switch (criteriaType) {
		case "categories":
			return <CategoriesSelector data={data} onChange={onChange} isReadOnly={isReadOnly} />;
		case "keywords":
			return <KeywordsSelector data={data} onChange={onChange} isReadOnly={isReadOnly} tagId={tagId} />;
		case "urls":
			return <UrlsSelector data={data} onChange={onChange} isReadOnly={isReadOnly} />;
		case "garm":
			return <GARMSelector data={data} onChange={onChange} isReadOnly={isReadOnly} />;
		case "sentiments":
			return <SentimentsSelector data={data} onChange={onChange} isReadOnly={isReadOnly} />;
		case "default":
			return <TaxonomySelector data={data} onChange={onChange} isReadOnly={isReadOnly} tagId={tagId} />;
		case "all":
		default:
			return null;
	}
}

// Custom criteria selector (QueryBuilder)
// Uses the same pattern as CustomIntegration in CustomUserDataCard:
// - onQueryChange={setQuery} for direct state updates (no ID stripping)
// - useEffect([query]) to notify parent, preserving react-querybuilder IDs
function CustomCriteriaSelector({ data, onChange, isReadOnly, filters }) {
	// Create initial query with one rule (matching original builder behavior).
	// `prepareRuleGroup` stamps stable react-querybuilder IDs onto the root group
	// and every rule. RQB keys rules by `rule.id`; without IDs it regenerates them
	// on every render, remounting the rule subtree and dropping input focus after
	// each keystroke on plain (integer/date) inputs (ASB-3219). Existing IDs (e.g.
	// a saved audience's filterBy) are preserved.
	const getInitialQuery = () => {
		const firstFilter = filters?.[0];
		const initialRule = firstFilter
			? {
					field: firstFilter.name,
					operator: firstFilter.operators?.[0]?.name || "=",
					value: firstFilter.inputType === "boolean" ? true : "",
				}
			: { field: "", operator: "=", value: "" };
		return prepareRuleGroup({ combinator: "or", rules: [initialRule] });
	};

	const [query, setQuery] = useState(() =>
		prepareRuleGroup(data?.custom?.filterBy || getInitialQuery())
	);

	// Reset query when switching between different custom criteria types
	const customType = data?.custom?.type;
	useEffect(() => {
		setQuery(prepareRuleGroup(data?.custom?.filterBy || getInitialQuery()));
	}, [customType]);

	// Notify parent when query changes — send query WITH IDs so that
	// if the component ever re-initializes, it gets IDs back from parent data
	useEffect(() => {
		onChange({
			custom: {
				...data?.custom,
				filterBy: query,
			},
		});
	}, [query]);

	if (!filters || filters.length === 0) {
		return <div className="criteria-selector">No filters configured for this criteria type.</div>;
	}

	return (
		<div className="criteria-selector custom-criteria-selector">
			<div className={query?.rules?.length > 0 ? "one-or-more-rules" : ""}>
				<QueryBuilder
					fields={filters}
					query={query}
					onQueryChange={setQuery}
					listsAsArrays
					translations={CONST.queryBuilderTranslations}
					combinators={CONST.queryBuilderCombinators}
					operators={CONST.queryBuilderOperators}
					showCombinatorsBetweenRules
					controlElements={{
						addGroupAction: () => null,
						valueSelector: QueryBuilderSelectField,
						valueEditor: QueryBuilderValueEditor,
						addRuleAction: QueryBuilderButton,
						removeRuleAction: QueryBuilderCloseButton,
						combinatorSelector: QueryBuilderCombinatorSelector,
					}}
					onRemove={(_ruleOrGroup, _path, q) => q.rules.length > 1}
				/>
			</div>
		</div>
	);
}

CustomCriteriaSelector.propTypes = {
	data: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	filters: PropTypes.array,
};

// Categories selector using TreeSelect (same as SyndicatedCard)
function CategoriesSelector({ data, onChange, isReadOnly }) {
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(true);

	// Build a set of parent category IDs from loaded tree data
	const parentIds = new Set(options.map((parent) => parent.value.split("|")[0]));

	// Transform selected data to TreeSelect format with labels (using labelInValue)
	const getSelectedValues = () => {
		if (!data?.categories) return [];
		return data.categories.map((cat) => {
			if (typeof cat === "string") {
				const [id, text] = cat.split("|");
				return { value: `${id}|${text}|false`, label: text };
			}
			const isParent = cat.isParent || parentIds.has(String(cat.id));
			const text = cat.text || String(cat.id);
			return { value: `${cat.id}|${text}|${isParent}`, label: isParent ? `All ${text}` : text };
		});
	};

	useEffect(() => {
		const loadCategories = async () => {
			try {
				setLoading(true);
				const categories = await getCategories();
				// Transform to TreeSelect format
				const treeData = categories.map((parent) => ({
					value: `${parent.id}|${parent.text}|true`,
					title: parent.text,
					children: parent.items
						.map((child) => ({
							value: `${child.id}|${child.text}|false`,
							title: child.text,
						}))
						.sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase())),
				}));
				setOptions(treeData);
			} catch (error) {
				console.error("Failed to load categories:", error);
			} finally {
				setLoading(false);
			}
		};
		loadCategories();
	}, []);

	const handleChange = (selectedValues) => {
		const categories = (selectedValues || []).map((item) => {
			const value = typeof item === "object" ? item.value : item;
			const [id, text, isParentStr] = value.split("|");
			return { id, text, isParent: isParentStr === "true" };
		});
		onChange({ categories });
	};

	return (
		<div className="criteria-selector">
			<div className="required">
				<label className="chips-label">Add Categories</label>
			</div>
			<div className="tree-select categories">
				<TreeSelect
					showSearch
					style={{ width: "100%" }}
					value={getSelectedValues()}
					dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
					placeholder={loading ? "Loading..." : "Select categories..."}
					treeCheckable
					allowClear
					variant="borderless"
					showCheckedStrategy={TreeSelect.SHOW_PARENT}
					onChange={handleChange}
					treeData={options}
					autoClearSearchValue={false}
					disabled={isReadOnly || loading}
					labelInValue
				/>
			</div>
		</div>
	);
}

CategoriesSelector.propTypes = {
	data: PropTypes.shape({
		categories: PropTypes.array,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
};

// Keywords selector
function KeywordsSelector({ data, onChange, isReadOnly, tagId }) {
	const [defaultOptions, setDefaultOptions] = useState([]);
	const [inputMode, setInputMode] = useState("Enter Keywords");
	const [inputValue, setInputValue] = useState("");
	const inputModes = ["Enter Keywords", "Paste Keywords"];

	const selectedKeywords = (data?.keywords || [])
		.filter((kw) => kw.trim() !== "")
		.map((kw) => ({
			label: kw,
			value: kw,
		}));

	// Build grouped options from API response (Matched + Similar)
	const buildGroupedOptions = (response) => {
		const matched = (response.data || []).map((kw) => ({ label: kw, value: kw }));
		const similar = (response.similar || []).map((kw) => ({ label: kw, value: kw }));
		const groups = [];
		if (matched.length > 0) {
			groups.push({ label: "Matched", options: matched });
		}
		if (similar.length > 0) {
			groups.push({ label: "Similar", options: similar });
		}
		return groups;
	};

	// Load initial default options
	useEffect(() => {
		const loadInitialOptions = async () => {
			if (!tagId) return;
			try {
				const response = await getKeywordsByTag(tagId, "");
				setDefaultOptions(buildGroupedOptions(response));
			} catch (error) {
				console.error("Failed to fetch keywords:", error);
			}
		};
		loadInitialOptions();
	}, [tagId]);

	// Async load options as user types
	const loadOptions = async (inputValue) => {
		if (!tagId) return [];
		try {
			const response = await getKeywordsByTag(tagId, inputValue);
			return buildGroupedOptions(response);
		} catch (error) {
			console.error("Failed to fetch keywords:", error);
			return [];
		}
	};

	const handleSelectChange = (selectedOptions) => {
		const keywords = (selectedOptions || []).map((item) => item.value);
		onChange({ keywords });
	};

	const handleTextareaChange = (e) => {
		const keywords = e.target.value.split("\n");
		onChange({ keywords });
	};

	return (
		<div className="criteria-selector">
			<div className="required">
				<label className="chips-label">Enter or Paste Keywords and Topics</label>
			</div>
			<div className="chips-info p5">Keywords or topics that match the content on your site</div>
			<SelectButton
				value={inputMode}
				onChange={(e) => setInputMode(e.value)}
				options={inputModes}
				className="input-mode-selector"
				unselectable={false}
			/>
			{inputMode === "Enter Keywords" ? (
				<div className="input-wrapper not-required enabled search">
					<AsyncCreatable
						name="keywords"
						classNamePrefix="select"
						className="select-field long"
						value={selectedKeywords}
						defaultOptions={defaultOptions}
						loadOptions={loadOptions}
						onChange={handleSelectChange}
						isDisabled={isReadOnly}
						isMulti={true}
						hideSelectedOptions={false}
						menuPlacement="auto"
						closeMenuOnSelect={false}
						placeholder="Type to search"
						inputValue={inputValue}
						onInputChange={(value, action) => {
							if (action.action === "input-change" || action.action === "menu-close") {
								setInputValue(value);
							}
							if (action.action === "set-value") {
								return inputValue;
							}
						}}
						formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
						theme={(theme) => ({
							...theme,
							borderRadius: 0,
							colors: {
								...theme.colors,
								primary25: "#B3CF79",
								primary: "#9ACA3C",
							},
						})}
					/>
					<div className="input--inner"></div>
				</div>
			) : (
				<div className="textarea-wrapper">
					<div className="chips-info p5">One keyword per line</div>
					<InputTextarea
						value={(data?.keywords || []).join("\n")}
						onChange={handleTextareaChange}
						rows={10}
						placeholder={"keyword1\nkeyword2\nkeyword3"}
						disabled={isReadOnly}
					/>
				</div>
			)}
		</div>
	);
}

KeywordsSelector.propTypes = {
	data: PropTypes.shape({
		keywords: PropTypes.array,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	tagId: PropTypes.string,
};

// URLs selector
function UrlsSelector({ data, onChange, isReadOnly }) {
	const getSelectedCondition = () => {
		const condition = data?.urls?.[0]?.condition;
		return CONST.urlConditions.find((c) => c.value === condition) || null;
	};

	const getSelectedValues = () => {
		return data?.urls?.[0]?.values || [];
	};

	const handleConditionChange = (option) => {
		onChange({
			urls: [
				{
					condition: option.value,
					values: getSelectedValues(),
				},
			],
		});
	};

	const handleValuesChange = (e) => {
		const values = e.target.value.split("\n");
		onChange({
			urls: [
				{
					condition: getSelectedCondition()?.value || "contains",
					values,
				},
			],
		});
	};

	return (
		<div className="criteria-selector">
			<Select
				label="Criteria"
				name="url-condition"
				options={CONST.urlConditions}
				selected={getSelectedCondition()}
				onSubmit={handleConditionChange}
				disabled={isReadOnly}
				required={true}
				search={false}
			/>
			<div className="textarea-wrapper">
				<div className="required">
					<label className="chips-label">Enter or Paste URLs or URL Fragments</label>
				</div>
				<div className="chips-info p5">One entry per line</div>
				<InputTextarea
					value={getSelectedValues().join("\n")}
					onChange={handleValuesChange}
					rows={10}
					placeholder={"URL1\nURL2\nURL3"}
					disabled={isReadOnly}
				/>
			</div>
		</div>
	);
}

UrlsSelector.propTypes = {
	data: PropTypes.shape({
		urls: PropTypes.arrayOf(
			PropTypes.shape({
				condition: PropTypes.string,
				values: PropTypes.array,
			})
		),
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
};

// GARM selector
function GARMSelector({ data, onChange, isReadOnly }) {
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(true);

	const selectedGarm = (data?.garm || []).map((g) => {
		if (typeof g === "string") {
			const [id, title] = g.split("|");
			return { value: g, label: title };
		}
		return { value: `${g.id}|${g.title}`, label: g.title };
	});

	useEffect(() => {
		const loadGARM = async () => {
			try {
				setLoading(true);
				const garmData = await getGARMTaxonomy();
				const opts = garmData.map((el) => ({
					value: `${el.id}|${el.title}`,
					label: el.title,
				}));
				setOptions(opts);
			} catch (error) {
				console.error("Failed to load GARM taxonomy:", error);
			} finally {
				setLoading(false);
			}
		};
		loadGARM();
	}, []);

	const handleChange = (selectedOptions) => {
		const garm = (selectedOptions || []).map((opt) => {
			const [id, title] = opt.value.split("|");
			return { id, title };
		});
		onChange({ garm });
	};

	return (
		<div className="criteria-selector">
			<Select
				name="garm"
				label="Brand Safety Categories"
				options={options}
				selected={selectedGarm}
				onSubmit={handleChange}
				disabled={loading || isReadOnly}
				required={true}
				multi={true}
				search={true}
				placeholder={loading ? "Loading..." : "Select brand safety categories..."}
			/>
		</div>
	);
}

GARMSelector.propTypes = {
	data: PropTypes.shape({
		garm: PropTypes.array,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
};

// Sentiments selector
function SentimentsSelector({ data, onChange, isReadOnly }) {
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(true);

	const selectedSentiments = (data?.sentiments || []).map((s) => {
		if (typeof s === "string") {
			const [id, title] = s.split("|");
			return { value: s, label: title };
		}
		return { value: `${s.id}|${s.title}`, label: s.title };
	});

	useEffect(() => {
		const loadSentiments = async () => {
			try {
				setLoading(true);
				const sentimentsData = await getSentimentTaxonomy();
				// Flatten the nested structure for Select component
				const opts = [];
				sentimentsData.forEach((el) => {
					if (el.items && el.items.length > 0) {
						el.items.forEach((item) => {
							opts.push({
								value: `${item.id}|${item.title}`,
								label: `${el.title} - ${item.title}`,
							});
						});
					} else {
						opts.push({
							value: `${el.id}|${el.title}`,
							label: el.title,
						});
					}
				});
				opts.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
				setOptions(opts);
			} catch (error) {
				console.error("Failed to load sentiments:", error);
			} finally {
				setLoading(false);
			}
		};
		loadSentiments();
	}, []);

	const handleChange = (selectedOptions) => {
		const sentiments = (selectedOptions || []).map((opt) => {
			const [id, title] = opt.value.split("|");
			return { id, title };
		});
		onChange({ sentiments });
	};

	return (
		<div className="criteria-selector">
			<Select
				name="sentiments"
				label="Sentiments"
				options={options}
				selected={selectedSentiments}
				onSubmit={handleChange}
				disabled={loading || isReadOnly}
				required={true}
				multi={true}
				search={true}
				placeholder={loading ? "Loading..." : "Select sentiments..."}
			/>
		</div>
	);
}

SentimentsSelector.propTypes = {
	data: PropTypes.shape({
		sentiments: PropTypes.array,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
};

// Taxonomy selector
function TaxonomySelector({ data, onChange, isReadOnly, tagId }) {
	const [defaultOptions, setDefaultOptions] = useState([]);
	const [inputValue, setInputValue] = useState("");

	const exceptions = ["of", "the", "and"];
	const convertToTitleCase = (str) => {
		if (!str) return "";
		return str
			.toLowerCase()
			.split("_")
			.map((word, i) => {
				return exceptions.includes(word) && i !== 0 ? word : word.charAt(0).toUpperCase() + word.substr(1);
			})
			.join(" ");
	};

	const formatNumber = (num) => {
		const formatter = Intl.NumberFormat("en-Us", { notation: "compact" });
		return formatter.format(num);
	};

	const getSelectedValues = () => {
		if (!data?.taxonomy?.values) return [];
		return data.taxonomy.values.map((el) => ({
			label: `${convertToTitleCase(el.property)}: ${el.value}`,
			value: `${el.property}|${el.value}`,
			pageViews: el.page_views_last_30_days,
		}));
	};

	const loadOptions = async (query) => {
		if (!tagId) return [];
		try {
			const response = await getTaxonomyByTag(tagId, query);
			return response.results.map((el) => ({
				label: `${convertToTitleCase(el.property)}: ${el.value}`,
				value: `${el.property}|${el.value}`,
				pageViews: el.page_views_last_30_days,
			}));
		} catch (error) {
			console.error("Failed to load taxonomy:", error);
			return [];
		}
	};

	useEffect(() => {
		loadOptions("").then(setDefaultOptions);
	}, [tagId]);

	const handleChange = (selected) => {
		const values = (selected || []).map((el) => {
			const [property, value] = el.value.split("|");
			return { property, value, page_views_last_30_days: el.pageViews };
		});
		onChange({ taxonomy: { values } });
	};

	return (
		<div className="criteria-selector">
			<div className="required">
				<label className="chips-label">Add Criteria</label>
			</div>
			<div className="input-wrapper required enabled search">
				<AsyncSelect
					classNamePrefix="select"
					className="select-field long"
					value={getSelectedValues()}
					defaultOptions={defaultOptions}
					loadOptions={loadOptions}
					onChange={handleChange}
					isDisabled={isReadOnly}
					isMulti={true}
					hideSelectedOptions={false}
					menuPlacement="auto"
					closeMenuOnSelect={false}
					placeholder="Type to search"
					inputValue={inputValue}
					onInputChange={(value, action) => {
						if (action.action === "input-change" || action.action === "menu-close") {
							setInputValue(value);
						}
						if (action.action === "set-value") {
							return inputValue;
						}
					}}
					theme={(theme) => ({
						...theme,
						borderRadius: 0,
						colors: {
							...theme.colors,
							primary25: "#B3CF79",
							primary: "#9ACA3C",
						},
					})}
					formatOptionLabel={(opt) => (
						<div className="taxonomy-option">
							<div className="taxonomy-label">{opt.label}</div>
							<div className="taxonomy-page-views">
								<img src={eyeIcon} alt="" className="eye-icon" />
								<span>{formatNumber(opt.pageViews || 0)}</span>
							</div>
						</div>
					)}
				/>
				<div className="input--inner"></div>
			</div>
		</div>
	);
}

TaxonomySelector.propTypes = {
	data: PropTypes.shape({
		taxonomy: PropTypes.shape({
			values: PropTypes.array,
		}),
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	tagId: PropTypes.string,
};

RealtimeSiteContentCard.propTypes = {
	data: PropTypes.shape({
		action: PropTypes.string,
		criteria: PropTypes.object,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	isCollapsed: PropTypes.bool,
	tagId: PropTypes.string,
	criteriaConfiguration: PropTypes.array,
	customTaxonomies: PropTypes.array,
	cardId: PropTypes.string,
};

RealtimeSiteContentCard.defaultProps = {
	data: { action: "include", criteria: { type: "all" } },
	isReadOnly: false,
	isCollapsed: false,
	tagId: null,
	criteriaConfiguration: [],
	customTaxonomies: [],
	cardId: "0",
};

// Export helper for getting selected count
RealtimeSiteContentCard.getSelectedCount = (data) => {
	const criteria = data?.criteria;
	if (!criteria) return 0;

	switch (criteria.type) {
		case "categories":
			return criteria.categories?.length || 0;
		case "keywords":
			return criteria.keywords?.length || 0;
		case "urls":
			return criteria.urls?.[0]?.values?.filter((v) => v.trim()).length || 0;
		case "garm":
			return criteria.garm?.length || 0;
		case "sentiments":
			return criteria.sentiments?.length || 0;
		case "default":
			return criteria.taxonomy?.values?.length || 0;
		case "custom":
			return (criteria.custom?.filterBy?.rules || []).reduce((count, rule) => {
				if (Array.isArray(rule.value)) {
					return count + rule.value.filter((v) => String(v).trim() !== "").length;
				}
				return count + (rule.value !== undefined && rule.value !== null && rule.value !== "" ? 1 : 0);
			}, 0);
		case "all":
			return 1;
		default:
			return 0;
	}
};

// Export helper for validating criteria data
RealtimeSiteContentCard.isValid = (data) => {
	const criteria = data?.criteria;
	if (!criteria || !criteria.type) return false;

	switch (criteria.type) {
		case "categories":
			return criteria.categories?.length > 0;
		case "keywords":
			return criteria.keywords?.filter((kw) => kw.trim()).length > 0;
		case "urls": {
			// Requires condition AND at least 1 non-empty value
			const urlEntry = criteria.urls?.[0];
			if (!urlEntry?.condition) return false;
			const validValues = urlEntry.values?.filter((v) => v.trim()) || [];
			return validValues.length > 0;
		}
		case "garm":
			return criteria.garm?.length > 0;
		case "sentiments":
			return criteria.sentiments?.length > 0;
		case "default":
			return criteria.taxonomy?.values?.length > 0;
		case "custom": {
			// Requires at least 1 rule, and each rule must have field, operator, and value
			const rules = criteria.custom?.filterBy?.rules || [];
			if (rules.length === 0) return false;
			return rules.every((rule) => {
				const hasField = rule.field && rule.field.trim() !== "";
				const hasOperator = rule.operator && rule.operator.trim() !== "";
				let hasValue;
				if (Array.isArray(rule.value)) {
					hasValue = rule.value.some((v) => String(v).trim() !== "");
				} else {
					hasValue = rule.value !== undefined && rule.value !== null && rule.value !== "";
				}
				return hasField && hasOperator && hasValue;
			});
		}
		case "all":
			return true;
		default:
			return false;
	}
};

export default RealtimeSiteContentCard;
